import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { CONFIG } from "../config";

const AverageMaintenanceCostGraph = ({ selectedDate, selectedStatus, selectedAgeRange }) => {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const tooltipRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 200 });
  const [data, setData] = useState([]);
  const [average_maintenance_cost, setAverageMaintenanceCost] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length) return;
      const { width } = entries[0].contentRect;
      setDimensions((prev) => ({ ...prev, width }));
    });

    if (wrapperRef.current) resizeObserver.observe(wrapperRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.AVERAGE_MAINTENANCE_GRAPH_URL}/${localStorage.getItem(
            "tensor-user-id"
          )}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRange}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );

        const result = await response.json();
        if (result.success) {
          setData(result.data.costs);
          setAverageMaintenanceCost(result.data.average_cost);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to fetch maintenance cost data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, selectedStatus, selectedAgeRange]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    const tooltip = d3.select(tooltipRef.current);

    const margin = { top: 30, right: 30, bottom: 40, left: 40 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const parseISODate = d3.utcParse("%Y-%m-%d");
    const parseSelectedDate = d3.utcParse("%m/%d/%Y");
    const formatDate = d3.utcFormat("%m/%d");

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.date))
      .range([0, width])
      .padding(0.5);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => Math.max(d.actual || 0, d.predicted || 0, d.budget)) * 1.2])
      .nice()
      .range([height, 0]);

    const yTicks = y.ticks(5);
    chart
      .selectAll(".y-grid-line")
      .data(yTicks.slice(1))
      .enter()
      .append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", (d) => y(d))
      .attr("y2", (d) => y(d))
      .attr("stroke", "#e5e7eb")
      .attr("stroke-dasharray", "4");

    chart
      .selectAll(".bar-actual")
      .data(data.filter((d) => d.actual !== null))
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.date))
      .attr("y", (d) => y(d.actual))
      .attr("width", x.bandwidth())
      .attr("height", (d) => height - y(d.actual))
      .attr("fill", "#032FCE")
      .on("mousemove", function (event, d) {
        tooltip
          .style("display", "block")
          .style("left", event.clientX + 10 + "px")
          .style("top", event.clientY - 20 + "px")
          .html(`
            <strong>Actual:</strong> $${Number(d.actual).toLocaleString()}<br/>
            <strong>Budget:</strong> $${Number(d.budget).toLocaleString()}
          `);
            })
      .on("mouseout", () => tooltip.style("display", "none"));

    chart
      .selectAll(".bar-predicted")
      .data(data.filter((d) => d.predicted !== null))
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.date))
      .attr("y", (d) => y(d.predicted))
      .attr("width", x.bandwidth())
      .attr("height", (d) => height - y(d.predicted))
      .attr("fill", "#6E91DC")
      .on("mousemove", function (event, d) {
        tooltip
          .style("display", "block")
          .style("left", event.clientX + 10 + "px")
          .style("top", event.clientY - 20 + "px")
          .html(`<strong>Predicted:</strong> $${parseFloat(d.predicted).toFixed(2)}<br/><strong>Budget:</strong> $${parseFloat(d.budget).toFixed(2)}`);
      })
      .on("mouseout", () => tooltip.style("display", "none"));

    const padding = 8;
    chart
      .selectAll(".budget-line")
      .data(data)
      .enter()
      .append("line")
      .attr("x1", (d) => x(d.date) + padding)
      .attr("x2", (d) => x(d.date) + x.bandwidth() - padding)
      .attr("y1", (d) => y(d.budget))
      .attr("y2", (d) => y(d.budget))
      .attr("stroke", "#B5D2FF")
      .attr("stroke-width", 3);

    chart
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3.axisBottom(x)
          .tickFormat((d) => formatDate(parseISODate(d)))
          .tickSize(0)
      )
      .call((g) => {
        g.selectAll("text").attr("dy", "1.5em").attr("fill", "#4b5563");
        g.select(".domain").attr("stroke", "#9ca3af");
      });

    chart
      .append("g")
      .call(d3.axisLeft(y).ticks(5).tickSize(0))
      .call((g) => {
        g.select(".domain").remove();
        g.selectAll("text").attr("fill", "#4b5563");
      });

    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    const selectedDateObj = parseSelectedDate(selectedDate);
    if (!selectedDateObj) return;

    const dateLabelsToShow = [
      { dateObj: selectedDateObj, color: "#032FCE" },
      { dateObj: new Date(selectedDateObj.getTime() + 7 * 24 * 60 * 60 * 1000), color: "#E5484D" }
    ];

    dateLabelsToShow.forEach(({ dateObj, color }) => {
      const matched = sortedData.find((d, i) => {
        const weekStart = parseISODate(d.date);
        const nextWeekStart = sortedData[i + 1] ? parseISODate(sortedData[i + 1].date) : null;
        if (weekStart) {
          const weekEnd = nextWeekStart
            ? new Date(nextWeekStart.getTime() - 1)
            : new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
          return dateObj >= weekStart && dateObj <= weekEnd;
        }
        return false;
      });

      if (matched && x(matched.date) !== undefined) {
        const baseX = x(matched.date);
        const barWidth = x.bandwidth();
        const dayOfWeek = dateObj.getUTCDay();
        const adjustedX = baseX + (barWidth * dayOfWeek) / 7;

        chart
          .append("line")
          .attr("x1", adjustedX + 2)
          .attr("x2", adjustedX + 2)
          .attr("y1", 0)
          .attr("y2", height)
          .attr("stroke", color)
          .attr("stroke-dasharray", "4")
          .attr("stroke-width", 1);

        chart
          .append("text")
          .attr("x", adjustedX)
          .attr("y", -10)
          .attr("text-anchor", "middle")
          .attr("fill", "#000000b3")
          .attr("font-size", "12px")
          .text(formatDate(dateObj));
      }
    });
  }, [data, dimensions, selectedDate]);

  return (
    <div ref={wrapperRef} className="relative w-full bg-white px-4 pt-4 rounded shadow">
      <h2 className="avg_maintainence_cost_head">Average Maintenance Cost</h2>

      {loading ? (
        <div className="flex justify-center items-center min-h-[220px]">
          <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </div>
      ) : (
        <>
          <p className="avg_maintainence_cost mb-2 mt-2">
            ${average_maintenance_cost.toLocaleString('en-US')}
          </p>

          <div className="flex gap-4 mb-2 text-sm">
            <div className="flex items-center gap-1 avg_maintainence_cost_legend">
              <span className="w-2 h-2 bar_blue_bg_color inline-block"></span> Actual
            </div>
            <div className="flex items-center gap-1 avg_maintainence_cost_legend">
              <span className="w-2 h-2 bar_blue_bg_predicted_color inline-block"></span> Predicted
            </div>
            <div className="flex items-center gap-1 avg_maintainence_cost_legend">
              <div className="w-2 h-1 bar_blue_bg_buget_color"></div> Budget Amount
            </div>
          </div>

          <svg ref={svgRef} width={dimensions.width} height={dimensions.height} />
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              background: "#fff",
              padding: "6px 10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              pointerEvents: "none",
              fontSize: "12px",
              display: "none",
              zIndex: 9999,
              boxShadow: "0px 0px 6px rgba(0,0,0,0.1)",
            }}
          ></div>
        </>
      )}
    </div>
  );
};

export default AverageMaintenanceCostGraph;
